import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../utils/auth';
import FunnelView from '../components/FunnelView';
import { TraceDashboardMini } from '../components/TraceDashboard';
import AppFooter from '../components/AppFooter';
import ReportPicker from '../components/ReportPicker';
import ReportMini from '../components/ReportMini';
import { KnowledgeIcon } from '../components/BrandIcons';
import { formatStampShort } from '../utils/datetime';
import '../styles/Dashboard.css';

// Which lens a panel card is showing: the trace preview (default) or the
// coverage funnel. Remembered per panel so the dashboard comes back as left.
const CARD_VIEW_KEY = (id) => `panelCardView:${id}`;

// Stored stamps are naive UTC and are shown as UTC, labelled — see utils/datetime.
const formatBuiltAt = (iso) => formatStampShort(iso);

/* Which panel kind each module's dashboard lists, and what it offers to create. The list is
   PAGED server-side, so the kind is sent as a query param rather than filtered here — a
   client-side filter would give short pages and a total counting panels this module hides. */
function Dashboard({ aiEnabled = true, knowledgeInNav = false, canManagePanels = true, isAdmin = false,
                     version, account, module = 'trace' }) {
  const isTrainingModule = module === 'training';
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage] = useState(1);
  const [itemsPerPage] = useState(500);
  // How many panel cards per row (1, 2 or 3) — set PER ROW, not per screen: a dashboard
  // usually wants one wide matrix at the top and three small report cards below it, which a
  // single screen-wide setting cannot express. `columns` is the default a row uses until it
  // is given one of its own; `rowCols[i]` is row i's override. Both persist per module, so
  // the Training and Traceability dashboards keep their own arrangement.
  const COLS_KEY = `dashboardColumns:${module}`;
  const ROWCOLS_KEY = `dashboardRowCols:${module}`;
  const [columns, setColumns] = useState(1);
  const [rowCols, setRowCols] = useState([]);

  // Read both back per module — this component is remounted with a different `module`
  // rather than unmounted, so the layout has to follow the prop, not the first render.
  useEffect(() => {
    const saved = parseInt(localStorage.getItem(COLS_KEY)
      // Migrate the old screen-wide key, which was shared by both dashboards.
      ?? localStorage.getItem('dashboardColumns'), 10);
    setColumns(saved === 2 || saved === 3 ? saved : 1);
    let rows = [];
    try {
      const raw = JSON.parse(localStorage.getItem(ROWCOLS_KEY) || '[]');
      if (Array.isArray(raw)) rows = raw.map((n) => (n === 2 || n === 3 ? n : 1));
    } catch (_) { rows = []; }
    setRowCols(rows);
  }, [module]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Resolve a panel's project_id/version_id to human-readable names.
  // Keyed maps built from /api/config/projects so existing panels (which don't
  // store the name) still display "Project Name (Version)".
  const [projectLookup, setProjectLookup] = useState({ byCombo: {}, byProject: {} });
  // Drag-and-drop reordering of panel cards. dragIndex = the card being dragged,
  // overIndex = the card it's currently hovering over (for the drop-line cue).
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  // "+ Add New Panel" is a menu: there are now three kinds of panel to create.
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // The instant-report gallery. A dozen reports would be a dozen more menu items, so the
  // menu offers one entry and the gallery is the surface that picks between them.
  const [pickerOpen, setPickerOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadPanels();
  }, [currentPage, itemsPerPage, module]);

  useEffect(() => {
    loadProjectNames();
  }, []);

  // Fixed-layout mode: toolbar pinned top, pagination pinned bottom, only the
  // panels list scrolls. Scoped via a body class so other pages keep normal
  // document scrolling.
  useEffect(() => {
    document.body.classList.add('dashboard-fixed');
    return () => document.body.classList.remove('dashboard-fixed');
  }, []);

  const loadProjectNames = async () => {
    try {
      const response = await fetch('/api/config/projects');
      const data = await response.json();
      if (data.IsSuccess && Array.isArray(data.projects)) {
        const byCombo = {};
        const byProject = {};
        data.projects.forEach(p => {
          const name = p.project_name || '';
          const label = p.version_label || '';
          if (p.project_id != null) byProject[String(p.project_id)] = name;
          if (p.project_id != null && p.version_id != null) {
            byCombo[`${p.project_id}|${p.version_id}`] = { name, version: label };
          }
        });
        setProjectLookup({ byCombo, byProject });
      }
    } catch (err) {
      console.error('Error loading project names:', err);
    }
  };

  // Returns a display string like "Cardiology Monitor (2.0)", falling back to
  // raw IDs when names aren't available yet.
  const resolveProjectLabel = (panel) => {
    const combo = projectLookup.byCombo[`${panel.project_id}|${panel.version_id}`];
    const name = (combo && combo.name) || projectLookup.byProject[String(panel.project_id)]
      || panel.project_name || '';
    const version = (combo && combo.version) || panel.version_label
      || (panel.version_id != null ? `v${panel.version_id}` : '');
    if (name && version) return `${name} (${version})`;
    if (name) return name;
    // Last-resort fallback to the old ID-based display
    return version ? `${panel.project_id} (${version})` : String(panel.project_id ?? '');
  };

  const loadPanels = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        `/api/config/panels?page=${currentPage}&limit=${itemsPerPage}&panel_type=${module}`
      );
      const data = await response.json();

      if (data.IsSuccess && data.panels) {
        setPanels(data.panels);
      } else {
        setError('Failed to load panels');
        setPanels([]);
      }
    } catch (err) {
      console.error('Error loading panels:', err);
      setError('Error: ' + err.message);
      setPanels([]);
    } finally {
      setLoading(false);
    }
  };

  // One place decides a panel's kind. The Training module lists TWO of them — its own panels
  // and the People panels that summarise them — so a boolean would have to be re-derived at
  // every call site, which is how a new type ends up routed to the trace matrix.
  const kindOf = (panel) => (panel?.panel_type || 'trace').toLowerCase();

  const ROUTES = {
    training: (id) => [`/training/${id}`, `/training/${id}/edit`],
    people: (id) => [`/training/people/${id}`, `/training/people/${id}/edit`],
    trace: (id) => [`/trace/matrix/${id}`, `/trace/config/${id}`],
    // A report has nothing to configure, so its "edit" route is its own view — the only
    // settings it owns are its name and who can see it, both changed from there.
    trace_report: (id) => [`/trace/report/${id}`, `/trace/report/${id}`],
    training_report: (id) => [`/training/report/${id}`, `/training/report/${id}`],
  };
  const routesFor = (panel) => (ROUTES[kindOf(panel)] || ROUTES.trace)(panel.id);

  const handleViewPanel = (panel) => navigate(routesFor(panel)[0]);
  const handleEditAnyPanel = (panel) => navigate(routesFor(panel)[1]);

  /** Create a People panel. There is no wizard: it has no filters to point at, so the menu
   *  item IS the whole creation flow — POST, then straight into the panel. */
  const createPeoplePanel = async () => {
    setAddMenuOpen(false);
    try {
      const r = await fetch('/api/people', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({ name: 'People', is_public: true, owner_only_edit: true }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `Could not create the panel (${r.status})`);
      navigate(`/training/people/${body.id}`);
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  /* The toolbar control still speaks for the whole screen: it sets the default AND drops
     every per-row override, so there is always one click that puts the dashboard back to a
     uniform grid. Without that, a row set to 3 six visits ago is unreachable from the
     toolbar and reads as a layout bug. */
  const setColumnLayout = (n) => {
    setColumns(n);
    setRowCols([]);
    localStorage.setItem(COLS_KEY, String(n));
    localStorage.removeItem(ROWCOLS_KEY);
  };

  const setRowLayout = (rowIndex, n) => {
    setRowCols((prev) => {
      // Rows before this one must keep the width they are drawn at, or changing row 3
      // re-flows rows 1-2 under it. Pad with the current default rather than leaving holes.
      const next = [...prev];
      while (next.length <= rowIndex) next.push(columnsForRow(next.length));
      next[rowIndex] = n;
      localStorage.setItem(ROWCOLS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const columnsForRow = (i) => rowCols[i] || columns;

  /* Panels are one ordered list; the rows are cut out of it left to right, each row taking
     as many cards as its own setting allows. Every card keeps its ABSOLUTE index — drag and
     drop reorders the list, not the row, so a row-relative index would move the wrong card. */
  const buildRows = () => {
    const rows = [];
    let i = 0;
    while (i < panels.length) {
      const n = columnsForRow(rows.length);
      rows.push({ cols: n, start: i, items: panels.slice(i, i + n) });
      i += n;
    }
    return rows;
  };

  // Persist the new card order for this account. The order is shared per
  // account, so we send the page's absolute offset (page + limit) with the ids
  // so display_order values stay consistent across pages on the server.
  const persistOrder = async (ordered) => {
    try {
      await fetch('/api/config/reorder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          ordered_ids: ordered.map(p => p.id),
          page: currentPage,
          limit: itemsPerPage,
        }),
      });
    } catch (err) {
      console.error('Failed to save panel order:', err);
    }
  };

  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(index)); } catch (_) { /* IE */ }
    // Drag the whole card as the ghost image, not just the small handle.
    const card = e.currentTarget.closest('.panel-card');
    if (card && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(card, 24, 24);
  };

  const handleDragOver = (e, index) => {
    if (dragIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== overIndex) setOverIndex(index);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const next = [...panels];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    setPanels(next);
    persistOrder(next);
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDeletePanel = async (setupId) => {
    if (!window.confirm('Delete this panel?')) return;

    try {
      const response = await fetch(`/api/config/${setupId}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': getCsrfToken() }
      });

      if (response.ok) {
        setPanels(panels.filter(p => p.id !== setupId));
      } else {
        alert('Delete failed');
      }
    } catch (error) {
      alert('Delete failed: ' + error.message);
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-toolbar">
        {/* Panel authoring needs the Orcanos dashboard-add right — a viewer reads only. */}
        {canManagePanels && (
          <div className="add-panel-menu">
            <button
              onClick={() => setAddMenuOpen((o) => !o)}
              className="btn-primary"
              aria-haspopup="menu"
              aria-expanded={addMenuOpen}
            >
              + Add New Panel <span className="add-panel-caret">▾</span>
            </button>
            {addMenuOpen && (
              <>
                {/* Click anywhere to dismiss — the backdrop sits under the menu, not over it */}
                <div className="add-panel-backdrop" onClick={() => setAddMenuOpen(false)} />
                {/* Each module offers only the panels it lists. Offering a Training panel from
                    the Traceability dashboard would create something this list can't show. */}
                <div className="add-panel-list" role="menu">
                  {!isTrainingModule && (
                    <button role="menuitem" onClick={() => { setAddMenuOpen(false); navigate('/trace/config'); }}>
                      <span className="apm-title">Traceability</span>
                      <span className="apm-sub">Requirements down to tests, level by level</span>
                    </button>
                  )}
                  {!isTrainingModule && aiEnabled && (
                    <button role="menuitem" onClick={() => { setAddMenuOpen(false); navigate('/trace/config/describe'); }}>
                      <span className="apm-title">✨ Describe with AI</span>
                      <span className="apm-sub">Write the chain in plain text and review the draft</span>
                    </button>
                  )}
                  {isTrainingModule && (
                    <button role="menuitem" onClick={() => { setAddMenuOpen(false); navigate('/training/new'); }}>
                      <span className="apm-title">🎓 Training Traceability</span>
                      <span className="apm-sub">Who owes training on a document — including who has no task at all</span>
                    </button>
                  )}
                  {/* No wizard behind this one: a People panel has no filters, so the click
                      creates it and opens it. It reads the snapshots the training panels
                      above have already built. */}
                  {isTrainingModule && (
                    <button role="menuitem" onClick={createPeoplePanel}>
                      <span className="apm-title">👥 People</span>
                      <span className="apm-sub">Everyone, their job title and their total training progress — nothing to set up</span>
                    </button>
                  )}
                  {/* Also no wizard, and no filters: an instant report reads the snapshots
                      the panels above have already built. The gallery is what picks which
                      of them — a dozen reports would be a dozen more items here. */}
                  <button role="menuitem" onClick={() => { setAddMenuOpen(false); setPickerOpen(true); }}>
                    <span className="apm-title">📊 Instant report</span>
                    <span className="apm-sub">Progress, gaps, quiz standing and cost — drawn from what this account already holds</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {/* Product Knowledge lives in the left nav; this button is only the fallback for an
            account that has no nav (traceability alone, no Ask Paul). */}
        {aiEnabled && !isTrainingModule && !knowledgeInNav && (
          <button onClick={() => navigate('/trace/knowledge')} className="btn-knowledge">
            <KnowledgeIcon size={18} /> Product Knowledge
          </button>
        )}
        {/* The Training module's two other surfaces. "My to-do" is deliberately shown
            to everyone — it is the trainee's own screen, and a trainee has no reason to
            be able to author a panel. */}
        {isTrainingModule && isAdmin && (
          <button onClick={() => navigate('/training/quizzes')} className="btn-knowledge">
            📝 Quizzes
          </button>
        )}
        {isTrainingModule && (
          <button onClick={() => navigate('/training/my')} className="btn-knowledge">
            🎓 My to-do
          </button>
        )}
        {/* The screen-wide control: it sets every row at once, which is also the only way
            back to a uniform grid once individual rows have been given their own width. */}
        <LayoutToggle
          value={columns}
          onChange={setColumnLayout}
          label="Panels per row on every row"
          titleFor={(n) => `${LAYOUT_WORD[n]} panel${n === 1 ? '' : 's'} per row — all rows`}
        />
      </div>

      <div className="dashboard-scroll">
        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading">Loading panels...</div>
        ) : panels.length === 0 ? (
          <div className="empty-state">
            <p>{canManagePanels
              ? 'No panels found. Click "+ Add New Panel" to get started.'
              : 'No panels have been shared with you yet.'}</p>
          </div>
        ) : (
          <div className="panels-list">
            {buildRows().map((row, rowIndex) => (
              <div className="panels-row" key={`row-${rowIndex}-${row.items[0].id}`}>
                <RowLayoutToggle
                  value={row.cols}
                  onChange={(n) => setRowLayout(rowIndex, n)}
                />
                <div className={`panels-row-grid cols-${row.cols}`}>
                  {row.items.map((panel, offset) => {
                    const index = row.start + offset;
                    const Card = {
                      training: TrainingCard, people: PeopleCard,
                      trace_report: ReportCard, training_report: ReportCard,
                    }[kindOf(panel)] || PanelCard;
                    return (
                      <Card
                        key={panel.id}
                        panel={panel}
                        projectLabel={resolveProjectLabel(panel)}
                        onView={() => handleViewPanel(panel)}
                        onEdit={() => handleEditAnyPanel(panel)}
                        onDelete={() => handleDeletePanel(panel.id)}
                        canManagePanels={canManagePanels}
                        onDragStartHandle={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        isDragging={dragIndex === index}
                        isDragOver={overIndex === index && dragIndex !== index}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {pickerOpen && (
        <ReportPicker
          module={module}
          onClose={() => setPickerOpen(false)}
          onCreated={(id, landedIn) => {
            setPickerOpen(false);
            // The gallery only offers what this dashboard lists, so `landedIn` is normally
            // this module — but the server, not the client, decides where a report is filed,
            // so navigate to where it actually went. Sending the user to this module's route
            // would 400 on the wrong guard and read as a panel that failed to save.
            navigate(`${landedIn === 'training' ? '/training' : '/trace'}/report/${id}`);
          }}
        />
      )}

      <AppFooter version={version} account={account} />
    </div>
  );
}

/* The 1 / 2 / 3-per-row control, drawn once. It appears twice — on the toolbar for the whole
   screen and again on every row — and two copies of the same three icons would drift apart
   the first time one of them was touched. */
const LAYOUT_WORD = { 1: 'One', 2: 'Two', 3: 'Three' };

const LAYOUT_ICONS = {
  1: (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <rect x="3" y="4" width="14" height="4.5" rx="1" />
      <rect x="3" y="11.5" width="14" height="4.5" rx="1" />
    </svg>
  ),
  2: (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <rect x="3" y="4" width="6" height="12" rx="1" />
      <rect x="11" y="4" width="6" height="12" rx="1" />
    </svg>
  ),
  3: (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <rect x="2" y="4" width="4" height="12" rx="1" />
      <rect x="8" y="4" width="4" height="12" rx="1" />
      <rect x="14" y="4" width="4" height="12" rx="1" />
    </svg>
  ),
};

function LayoutToggle({ value, onChange, label, titleFor, className = '' }) {
  return (
    <div className={`layout-toggle ${className}`} role="group" aria-label={label}>
      {[1, 2, 3].map((n) => (
        <button
          key={n}
          type="button"
          className={`layout-btn ${value === n ? 'active' : ''}`}
          onClick={() => onChange(n)}
          title={titleFor(n)}
          aria-pressed={value === n}
        >
          {LAYOUT_ICONS[n]}
        </button>
      ))}
    </div>
  );
}

/* One row's own width. Kept faint until the row is hovered: a dashboard of eight rows would
   otherwise carry eight copies of the same control competing with the cards for attention. */
function RowLayoutToggle({ value, onChange }) {
  return (
    <LayoutToggle
      className="row-layout-toggle"
      value={value}
      onChange={onChange}
      label="Panels in this row"
      titleFor={(n) => `${LAYOUT_WORD[n]} panel${n === 1 ? '' : 's'} in this row`}
    />
  );
}

// Exported for the Risk home, which shows Traceability panels whose level 1 is Risk (v4.20.0).
// There `onDelete` only takes the card off the Risk home — `deleteTitle` says so — and the
// card is not draggable (`onDragStartHandle` absent).
export function PanelCard({
  panel, projectLabel, onView, onEdit, onDelete, canManagePanels = true,
  onDragStartHandle, onDragOver, onDrop, onDragEnd, isDragging, isDragOver,
  deleteTitle = 'Delete',
}) {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // 'trace' = the matrix preview (page 1) · 'funnel' = the cached coverage funnel ·
  // 'dash' = the cached Traceability Dashboard (v4.27.0) — filled by the same build as the funnel
  const [cardView, setCardView] = useState(() => {
    const v = localStorage.getItem(CARD_VIEW_KEY(panel.id));
    return v === 'funnel' || v === 'dash' ? v : 'trace';
  });
  const [dash, setDash] = useState(null);          // GET /dashboard: { cached, summary, trend, built_at }
  const [dashLoading, setDashLoading] = useState(false);
  const [dashError, setDashError] = useState('');
  const [funnel, setFunnel] = useState(null);      // { cached, funnel, built_at }
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelError, setFunnelError] = useState('');
  const [funnelProgress, setFunnelProgress] = useState(null); // null | 0-100
  const [funnelMessage, setFunnelMessage] = useState('');
  const funnelPollRef = useRef(null);

  // Each lens loads only when it's actually shown, so a dashboard of funnel
  // cards doesn't also fetch every matrix preview (and vice versa).
  useEffect(() => {
    if (cardView === 'trace' && preview === null) loadPreview();
    if (cardView === 'funnel' && funnel === null) loadFunnel();
    if (cardView === 'dash' && dash === null) loadDash();
  }, [panel.id, cardView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop polling if the card unmounts mid-build (e.g. the user pages away)
  useEffect(() => () => { if (funnelPollRef.current) clearInterval(funnelPollRef.current); }, []);

  const switchView = (v) => {
    setCardView(v);
    localStorage.setItem(CARD_VIEW_KEY(panel.id), v);
  };

  const loadDash = async () => {
    setDashLoading(true);
    setDashError('');
    try {
      const resp = await fetch(`/api/matrix/${panel.id}/dashboard`, { credentials: 'include' });
      if (resp.ok) setDash(await resp.json());
      else setDashError('Failed to load the dashboard');
    } catch (err) {
      setDashError('Error: ' + err.message);
    } finally {
      setDashLoading(false);
    }
  };

  // Full View opens the full page on the view this card shows.
  const openFull = () => {
    const mode = { trace: 'matrix', funnel: 'funnel', dash: 'dashboard' }[cardView] || 'matrix';
    try { sessionStorage.setItem('matrixViewMode', mode); } catch { /* ignore */ }
    onView();
  };

  const loadFunnel = async () => {
    setFunnelLoading(true);
    setFunnelError('');
    try {
      const resp = await fetch(`/api/matrix/${panel.id}/funnel`, { credentials: 'include' });
      if (resp.ok) setFunnel(await resp.json());
      else setFunnelError('Failed to load funnel');
    } catch (err) {
      setFunnelError('Error: ' + err.message);
    } finally {
      setFunnelLoading(false);
    }
  };

  // The funnel is an aggregate over the WHOLE panel (every page, every level),
  // so it's a background job — never built implicitly on load. Progress is
  // polled on the shared export-jobs endpoint, as in the full Funnel view.
  const buildFunnel = async () => {
    setFunnelError('');
    try {
      const resp = await fetch(`/api/matrix/${panel.id}/funnel/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': getCsrfToken() },
      });
      if (resp.status === 202) {
        const job = await resp.json();
        setFunnelProgress(0);
        setFunnelMessage(job.message || 'Building...');
        pollFunnelJob(job.job_id);
      } else {
        const e = await resp.json().catch(() => ({}));
        setFunnelError(e.detail || 'Failed to start build');
      }
    } catch (err) {
      setFunnelError('Error: ' + err.message);
    }
  };

  const pollFunnelJob = (jobId) => {
    if (funnelPollRef.current) clearInterval(funnelPollRef.current);
    funnelPollRef.current = setInterval(async () => {
      try {
        const resp = await fetch(`/api/matrix/export-jobs/${jobId}`, { credentials: 'include' });
        if (!resp.ok) { clearInterval(funnelPollRef.current); setFunnelProgress(null); return; }
        const job = await resp.json();
        setFunnelProgress(job.progress || 0);
        setFunnelMessage(job.message || '');
        if (job.status === 'completed') {
          clearInterval(funnelPollRef.current);
          setFunnelProgress(null);
          setFunnelMessage('');
          loadFunnel();
          loadDash();      // one build fills both the funnel and the dashboard
        } else if (job.status === 'error') {
          clearInterval(funnelPollRef.current);
          setFunnelProgress(null);
          setFunnelError(job.error || 'Build failed');
        }
      } catch (err) {
        clearInterval(funnelPollRef.current);
        setFunnelProgress(null);
      }
    }, 700);
  };

  const loadPreview = async (refresh = false) => {
    if (refresh) setRefreshing(true); else setPreviewLoading(true);
    setPreviewError('');
    try {
      const response = await fetch('/api/matrix/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({
          setup_id: panel.id,
          page: 1,
          page_size: 25,
          refresh
        })
      });

      const data = await response.json();
      if (data.IsSuccess) {
        setPreview(data);
      } else {
        setPreviewError(data.error || 'Failed to load preview');
      }
    } catch (err) {
      console.error('Error loading preview:', err);
      setPreviewError('Error loading preview');
    } finally {
      setPreviewLoading(false);
      setRefreshing(false);
    }
  };

  // Parse levels from the panel
  const levels = panel.levels ? (typeof panel.levels === 'string' ? JSON.parse(panel.levels) : panel.levels) : [];
  const configuredLevelCount = 1 + levels.length; // L1 + additional levels
  // Build level labels using custom names if available, fallback to L1, L2, etc.
  const configuredLevels = [panel.base_level_name || 'L1', ...levels.map((l, i) => l.level_name || `L${i + 2}`)].slice(0, configuredLevelCount);

  return (
    <div
      className={`panel-card panel-card-open${isDragging ? ' is-dragging' : ''}${isDragOver ? ' is-drop-target' : ''}`}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      {...openOnCardClick(onView)}
    >
      <div className="panel-header">
        {/* Reordering writes the shared per-account order — same gate as the other panel
            writes, so a viewer gets a static (non-draggable) handle. */}
        {onDragStartHandle && <span
          className="panel-drag-handle"
          draggable={canManagePanels}
          onDragStart={canManagePanels ? onDragStartHandle : undefined}
          title={canManagePanels ? 'Drag to reorder' : undefined}
          aria-label={canManagePanels ? 'Drag to reorder panel' : undefined}
          role={canManagePanels ? 'button' : undefined}
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <circle cx="7" cy="5" r="1.5" /><circle cx="13" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" /><circle cx="13" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" /><circle cx="13" cy="15" r="1.5" />
          </svg>
        </span>}
        <div className="panel-title-section">
          <h3>{panel.name}</h3>
          <p className="panel-meta">
            <span className="panel-project">{projectLabel}</span>
            <span className="meta-separator">•</span>
            <span>{configuredLevelCount} levels</span>
            <span className="meta-separator">•</span>
            <span className={panel.is_public ? 'badge-public' : 'badge-private'}>
              {panel.is_public ? 'Public' : 'Private'}
            </span>
          </p>
        </div>
        <div className="panel-actions-header">
          {/* Same look as the matrix toolbar's Matrix/Funnel/Graph toggle
              (MatrixPage .view-toggle), sized down for a card header. */}
          <div className="pcv-toggle" role="tablist" aria-label="Panel view">
            <button
              type="button" role="tab" aria-selected={cardView === 'dash'}
              className={cardView === 'dash' ? 'active' : ''}
              onClick={() => switchView('dash')}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 14h12" /><rect x="3" y="8" width="2.4" height="5" /><rect x="6.8" y="4" width="2.4" height="9" /><rect x="10.6" y="6" width="2.4" height="7" /></svg>
              Dashboard
            </button>
            <button
              type="button" role="tab" aria-selected={cardView === 'trace'}
              className={cardView === 'trace' ? 'active' : ''}
              onClick={() => switchView('trace')}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><rect x="1.5" y="1.5" width="13" height="13" rx="1.5" /><path d="M1.5 6h13M1.5 10.5h13M6 1.5v13" /></svg>
              Trace
            </button>
            <button
              type="button" role="tab" aria-selected={cardView === 'funnel'}
              className={cardView === 'funnel' ? 'active' : ''}
              onClick={() => switchView('funnel')}
            >
              <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M1 2h14l-5 6v5l-4 2V8z" /></svg>
              Funnel
            </button>
          </div>
          {cardView === 'trace' ? (
            <button
              onClick={() => loadPreview(true)}
              className="btn-action"
              disabled={refreshing || previewLoading}
              title="Refresh this panel from Orcanos"
            >
              {refreshing ? '↻ Refreshing…' : '↻ Refresh'}
            </button>
          ) : (
            <button
              onClick={buildFunnel}
              className="btn-action"
              disabled={funnelProgress !== null || funnelLoading}
              title={cardView === 'dash' ? 'Rebuild the dashboard from the full matrix' : 'Rebuild the coverage funnel from the full matrix'}
            >
              {funnelProgress !== null ? '↻ Building…'
                : ((cardView === 'dash' ? dash && dash.cached : funnel && funnel.cached) ? '↻ Rebuild' : '↻ Build')}
            </button>
          )}
          <button onClick={openFull} className="btn-action">Full View</button>
          {canManagePanels && (
            <>
              <button onClick={onEdit} className="btn-action btn-action-icon" title="Edit" aria-label="Edit">✎</button>
              <button onClick={onDelete} className="btn-action btn-action-icon btn-action-danger" title={deleteTitle} aria-label={deleteTitle}>🗑</button>
            </>
          )}
        </div>
      </div>

      <div className="panel-preview">
        {cardView === 'dash' ? (
          <TraceDashboardMini
            dash={dash}
            loading={dashLoading}
            error={dashError || funnelError}
            progress={funnelProgress}
            message={funnelMessage}
            onBuild={buildFunnel}
          />
        ) : cardView === 'funnel' ? (
          <FunnelPreview
            funnel={funnel}
            loading={funnelLoading}
            error={funnelError}
            progress={funnelProgress}
            message={funnelMessage}
            onBuild={buildFunnel}
          />
        ) : previewLoading ? (
          <div className="preview-loading">Loading preview...</div>
        ) : previewError ? (
          <div className="preview-error">{previewError}</div>
        ) : preview && preview.rows && preview.rows.length > 0 ? (
          <MatrixPreview
            rows={preview.rows.slice(0, 25)}
            levels={(preview.configured_levels && preview.configured_levels.length)
              ? preview.configured_levels.map(l => ({
                  name: l.name || `L${l.level_num}`,
                  isTestRun: !!l.is_test_run,
                }))
              : configuredLevels.map(name => ({ name, isTestRun: false }))}
          />
        ) : (
          <div className="preview-empty">No data to display</div>
        )}
      </div>
    </div>
  );
}

/**
 * A Training Traceability panel's card.
 *
 * Reads the cached snapshot only — `GET /report` never builds. A build reads three whole
 * filters and is a background job; it is started from the panel view, which is where the user
 * is waiting for it anyway. A dashboard of training cards must never fire one build per card.
 */
function TrainingCard({
  panel, projectLabel, onView, onEdit, onDelete, canManagePanels = true,
  onDragStartHandle, onDragOver, onDrop, onDragEnd, isDragging, isDragOver,
}) {
  const [snap, setSnap] = useState(null);       // { cached, report, built_at }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`/api/training/${panel.id}/report`, { credentials: 'include' });
        if (!alive) return;
        if (r.ok) setSnap(await r.json());
        else setError('Could not read this panel');
      } catch (err) {
        if (alive) setError('Error: ' + err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [panel.id]);

  const f = snap && snap.cached && snap.report ? snap.report.funnel : null;

  /* `panel-card-compact`: this preview is four numbers and one line of text — a fraction of
     the 480px a trace card needs for its matrix, so the fixed height left the rest as blank
     card. Compact is a SHORTER FIXED height, not auto: the People and report cards carry the
     same class, so every card on the Training dashboard stays exactly the same size. */
  return (
    <div
      className={`panel-card panel-card-compact panel-card-open${isDragging ? ' is-dragging' : ''}${isDragOver ? ' is-drop-target' : ''}`}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      {...openOnCardClick(onView)}
    >
      <div className="panel-header">
        <span
          className="panel-drag-handle"
          draggable={canManagePanels}
          onDragStart={canManagePanels ? onDragStartHandle : undefined}
          title={canManagePanels ? 'Drag to reorder' : undefined}
          aria-label={canManagePanels ? 'Drag to reorder panel' : undefined}
          role={canManagePanels ? 'button' : undefined}
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <circle cx="7" cy="5" r="1.5" /><circle cx="13" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" /><circle cx="13" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" /><circle cx="13" cy="15" r="1.5" />
          </svg>
        </span>
        <div className="panel-title-section">
          <h3>{panel.name}</h3>
          <p className="panel-meta">
            <span className="badge-training">🎓 Training</span>
            <span className="meta-separator">•</span>
            <span className="panel-project">{projectLabel}</span>
            <span className="meta-separator">•</span>
            <span className={panel.is_public ? 'badge-public' : 'badge-private'}>
              {panel.is_public ? 'Public' : 'Private'}
            </span>
          </p>
        </div>
        <div className="panel-actions-header">
          <button onClick={onView} className="btn-action">Full View</button>
          {canManagePanels && (
            <>
              <button onClick={onEdit} className="btn-action btn-action-icon" title="Edit" aria-label="Edit">✎</button>
              <button onClick={onDelete} className="btn-action btn-action-icon btn-action-danger" title="Delete" aria-label="Delete">🗑</button>
            </>
          )}
        </div>
      </div>

      <div className="panel-preview">
        {loading ? (
          <div className="preview-loading">Loading…</div>
        ) : error ? (
          <div className="preview-error">{error}</div>
        ) : !f ? (
          <div className="tc-empty">
            <p>No snapshot yet.</p>
            <button className="btn-action" onClick={onView}>Open to build the report</button>
          </div>
        ) : (
          <>
            <div className="tc-kpis">
              <div className="tc-kpi miss"><b>{f.missing}</b><span>People with no task</span></div>
              <div className="tc-kpi open"><b>{f.open}</b><span>Open</span></div>
              <div className="tc-kpi ok"><b>{f.done}</b><span>Signed</span></div>
              <div className="tc-kpi doc"><b>{f.in_scope}</b><span>Documents</span></div>
            </div>
            <p className="tc-note">
              {f.expected} trainees expected across {f.in_scope} documents
              {snap.built_at && <> · snapshot {formatBuiltAt(snap.built_at)}</>}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * A People panel's card.
 *
 * Costs zero Orcanos calls: the roster is aggregated from the training snapshots already in
 * the database, so unlike a training card there is nothing here that could ever start a
 * build. `limit=1` because the card wants only `totals` and `coverage`, both of which the
 * server computes over the whole panel and never over the page.
 */
function PeopleCard({
  panel, onView, onEdit, onDelete, canManagePanels = true,
  onDragStartHandle, onDragOver, onDrop, onDragEnd, isDragging, isDragOver,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`/api/people/${panel.id}?limit=1`, { credentials: 'include' });
        if (!alive) return;
        if (r.ok) setData(await r.json());
        else setError('Could not read this panel');
      } catch (err) {
        if (alive) setError('Error: ' + err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [panel.id]);

  const t = data?.totals;
  const unbuilt = (data?.coverage || []).filter((c) => !c.built);

  return (
    <div
      className={`panel-card panel-card-compact panel-card-open${isDragging ? ' is-dragging' : ''}${isDragOver ? ' is-drop-target' : ''}`}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      {...openOnCardClick(onView)}
    >
      <div className="panel-header">
        <span
          className="panel-drag-handle"
          draggable={canManagePanels}
          onDragStart={canManagePanels ? onDragStartHandle : undefined}
          title={canManagePanels ? 'Drag to reorder' : undefined}
          aria-label={canManagePanels ? 'Drag to reorder panel' : undefined}
          role={canManagePanels ? 'button' : undefined}
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <circle cx="7" cy="5" r="1.5" /><circle cx="13" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" /><circle cx="13" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" /><circle cx="13" cy="15" r="1.5" />
          </svg>
        </span>
        <div className="panel-title-section">
          <h3>{panel.name}</h3>
          <p className="panel-meta">
            <span className="badge-people">👥 People</span>
            <span className="meta-separator">•</span>
            <span className="panel-project">All training panels</span>
            <span className="meta-separator">•</span>
            <span className={panel.is_public ? 'badge-public' : 'badge-private'}>
              {panel.is_public ? 'Public' : 'Private'}
            </span>
          </p>
        </div>
        <div className="panel-actions-header">
          <button onClick={onView} className="btn-action">Full View</button>
          {canManagePanels && (
            <>
              <button onClick={onEdit} className="btn-action btn-action-icon" title="Edit" aria-label="Edit">✎</button>
              <button onClick={onDelete} className="btn-action btn-action-icon btn-action-danger" title="Delete" aria-label="Delete">🗑</button>
            </>
          )}
        </div>
      </div>

      <div className="panel-preview">
        {loading ? (
          <div className="preview-loading">Loading…</div>
        ) : error ? (
          <div className="preview-error">{error}</div>
        ) : !t || t.panels_built === 0 ? (
          <div className="tc-empty">
            {/* An unbuilt source panel and a compliant one look identical on a roster, so the
                card says which it is showing rather than printing a confident zero. */}
            <p>No training panel has been built yet, so there is nothing to summarise.</p>
            <button className="btn-action" onClick={onView}>Open the panel</button>
          </div>
        ) : (
          <>
            <div className="tc-kpis">
              <div className="tc-kpi doc"><b>{t.people}</b><span>People</span></div>
              <div className="tc-kpi ok"><b>{t.compliant}</b><span>Fully signed</span></div>
              <div className="tc-kpi open"><b>{t.outstanding}</b><span>Outstanding</span></div>
              <div className="tc-kpi miss">
                <b>{t.overdue === null ? '—' : t.overdue}</b><span>Overdue</span>
              </div>
            </div>
            <p className="tc-note">
              {t.pct}% of {t.obligations} required trainings signed · built from{' '}
              {t.panels_built} of {t.panels} panel{t.panels === 1 ? '' : 's'}
              {unbuilt.length > 0 && <> · ⚠ {unbuilt.length} never built</>}
              {t.built_at && <> · oldest snapshot {formatBuiltAt(t.built_at)}</>}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Make a whole card open its panel.
 *
 * The card is mostly a large inert rectangle with one small "Full View" button in the corner,
 * and everything on it is *about* the panel it opens — so the whole surface should be the
 * target, not a 70px button.
 *
 * The interactive descendants are excluded by ONE predicate rather than by stopping
 * propagation on each of them: a card carries buttons, links, selects and a drag handle today
 * and will carry more later, and a per-element rule is a list somebody has to remember to add
 * to. `closest()` also covers a click on an icon *inside* a button, which is where the
 * per-element version leaks.
 */
// `.fseg` is a funnel segment — a plain div that becomes clickable when the funnel is NOT
// compact. It is always compact on a card today, so this is insurance against that changing
// somewhere far from here and silently turning a drill-down into a navigation.
const INTERACTIVE = 'button, a, select, input, textarea, label, .panel-drag-handle, .fseg, [data-no-card-open]';

function openOnCardClick(onView) {
  const shouldIgnore = (e) => (
    e.target.closest(INTERACTIVE)
    // A click that ends a text selection is not a click on the card.
    || String(window.getSelection?.() || '').length > 0
  );
  return {
    role: 'button',
    tabIndex: 0,
    onClick: (e) => { if (!shouldIgnore(e)) onView(); },
    onKeyDown: (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
        e.preventDefault();
        onView();
      }
    },
  };
}

/* The report envelope's tone names, mapped to the KPI classes this dashboard already has.
   `bad` shares the `miss` tile because the card has four colours and the report has five —
   the distinction between "behind" and "badly behind" is on the full view, not here. */
const TONE_CLASS = { ok: 'ok', open: 'open', miss: 'miss', bad: 'miss', mute: 'doc' };

/**
 * An instant report's dashboard card — its KPI tiles, and how fresh they are.
 *
 * The card reads the SAME endpoint the full view does, so the two can never disagree; the
 * report is a grouping of snapshots that are themselves cached, so there is no cheaper
 * summary endpoint to be had and no build this could accidentally trigger.
 *
 * `sources` is what turns four confident numbers into honest ones: a contributing panel
 * that has never been built contributes nothing at all, and its absence is invisible in
 * every total. The card names them rather than printing the total as if it were complete.
 */
function ReportCard({
  panel, onView, onDelete, canManagePanels = true,
  onDragStartHandle, onDragOver, onDrop, onDragEnd, isDragging, isDragOver,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`/api/reports/${panel.id}`, { credentials: 'include' });
        const body = await r.json().catch(() => ({}));
        if (!alive) return;
        if (r.ok) setData(body);
        else setError(body.detail || 'Could not read this report');
      } catch (err) {
        if (alive) setError('Error: ' + err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [panel.id]);

  const unbuilt = (data?.sources || []).filter((s) => !s.built);

  return (
    <div
      className={`panel-card panel-card-compact panel-card-open${isDragging ? ' is-dragging' : ''}${isDragOver ? ' is-drop-target' : ''}`}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      {...openOnCardClick(onView)}
    >
      <div className="panel-header">
        <span
          className="panel-drag-handle"
          draggable={canManagePanels}
          onDragStart={canManagePanels ? onDragStartHandle : undefined}
          title={canManagePanels ? 'Drag to reorder' : undefined}
          aria-label={canManagePanels ? 'Drag to reorder panel' : undefined}
          role={canManagePanels ? 'button' : undefined}
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <circle cx="7" cy="5" r="1.5" /><circle cx="13" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" /><circle cx="13" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" /><circle cx="13" cy="15" r="1.5" />
          </svg>
        </span>
        <div className="panel-title-section">
          <h3>{panel.name}</h3>
          <p className="panel-meta">
            <span className="badge-report">📊 Report</span>
            <span className="meta-separator">•</span>
            <span className="panel-project">{data?.blurb || 'Nothing to set up'}</span>
            <span className="meta-separator">•</span>
            <span className={panel.is_public ? 'badge-public' : 'badge-private'}>
              {panel.is_public ? 'Public' : 'Private'}
            </span>
          </p>
        </div>
        <div className="panel-actions-header">
          <button onClick={onView} className="btn-action">Full View</button>
          {canManagePanels && (
            <button onClick={onDelete} className="btn-action btn-action-icon btn-action-danger" title="Delete" aria-label="Delete">🗑</button>
          )}
        </div>
      </div>

      <div className="panel-preview">
        {loading ? (
          <div className="preview-loading">Loading…</div>
        ) : error ? (
          <div className="preview-error">{error}</div>
        ) : (
          <>
            {/* A miniature of the report's own primary block. Four numbers and a sentence made
                twelve report cards indistinguishable — you had to open each one to find out
                which was which. Drawn from the same payload the full view uses, so the card
                can never show something the report does not. */}
            <ReportMini data={data} />
            <p className="tc-note">
              {data?.sources?.length
                ? <>
                    read from {data.sources.filter((s) => s.built).length} of{' '}
                    {data.sources.length} panel{data.sources.length === 1 ? '' : 's'}
                    {data.built_at && <> · oldest snapshot {formatBuiltAt(data.built_at)}</>}
                    {unbuilt.length > 0 && <> · ⚠ {unbuilt.length} never built</>}
                  </>
                : "read live from this account's own records"}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// The funnel lens on a panel card: the stage bars from the cached snapshot and
// silently rebuilds page 1 on demand), the funnel is only ever built when the
// user asks — it aggregates the entire panel, so it must not fire on load.
function FunnelPreview({ funnel, loading, error, progress, message, onBuild }) {
  const cached = funnel && funnel.cached && funnel.funnel;
  const building = progress !== null;

  if (loading && !cached) return <div className="preview-loading">Loading funnel...</div>;

  if (!cached) {
    return (
      <div className="pcv-funnel-empty">
        {building ? (
          <>
            <div className="pcv-build-label">{message || 'Building...'} {progress}%</div>
            <div className="pcv-pbar"><div className="pcv-pfill" style={{ width: `${progress}%` }} /></div>
          </>
        ) : (
          <>
            <p className="pcv-empty-title">No coverage funnel yet</p>
            <p className="pcv-empty-sub">Built from the whole panel — every level, every page.</p>
            <button className="btn-primary pcv-build-btn" onClick={onBuild}>Build funnel</button>
            {error && <p className="preview-error">{error}</p>}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="pcv-funnel">
      <FunnelView funnel={funnel.funnel} compact />
      <div className="pcv-funnel-foot">
        {building ? (
          <span className="pcv-stale">Rebuilding — {message || 'working'} {progress}%</span>
        ) : error ? (
          <span className="preview-error">{error}</span>
        ) : (
          <span className="pcv-stale">Snapshot built {formatBuiltAt(funnel.built_at)}</span>
        )}
      </div>
    </div>
  );
}

// The run status/result column, whatever the tenant named it. Orcanos ships
// "Last Test Run Result", but a filter may carry "Test Run Status" (or several
// run columns at once) — match any column naming a run AND a status/result.
// "Last Test Run Version" is deliberately not a match.
const RUN_RESULT_COL = /run.*(status|result)/i;

// First non-empty run status/result on the item, markup stripped. '' when the
// level's filter doesn't carry one — the caller then falls back to the key.
const runResultOf = (item) => {
  const cols = item && item.columns;
  if (!cols) return '';
  for (const [name, value] of Object.entries(cols)) {
    if (!RUN_RESULT_COL.test(name)) continue;
    const text = String(value == null ? '' : value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (text && text !== '-') return text;
  }
  return '';
};

// Mirrors testRunPillClass in MatrixPage.jsx (same .status-pill.tr-* classes, which
// are global). Order matters: "Fail (Stop)" must be tested before plain "Fail".
const testRunPillClass = (value) => {
  const v = String(value).toLowerCase();
  if (/fail\s*\(?\s*stop/.test(v)) return 'tr-failstop';
  if (/block/.test(v)) return 'tr-blocker';
  if (/fail/.test(v)) return 'tr-fail';
  if (/pass/.test(v)) return 'tr-pass';
  if (/(no\s*run|not\s*in\s*execution|not\s*executed)/.test(v)) return 'tr-norun';
  return 'tr-neutral';
};

function MatrixPreview({ rows, levels: levelDefs }) {
  // Levels arrive as {name, isTestRun}; tolerate a bare name (legacy callers).
  const levels = levelDefs.map(l => (typeof l === 'string' ? { name: l, isTestRun: false } : l));

  // Render an item as "Key: Name" (e.g. "UR-16281: The system shall...").
  // The Key column may carry a suffix like "UR-16281 (7677)" — keep only the prefix.
  // Empty cells render blank (no "-" placeholder).
  //
  // A Test Run repeats its Test Case's key (they share it — Critical Note #13), so
  // on a card that key is pure noise. When the run level's filter carries a run
  // status/result column, show that value instead, as a coloured pill. With no such
  // column (or an empty value) the key renders as before.
  const renderItem = (item, isTestRun) => {
    if (!item) return null;
    if (typeof item === 'string') return item;
    if (isTestRun) {
      const result = runResultOf(item);
      if (result) {
        return <span className={`status-pill ${testRunPillClass(result)}`}>{result}</span>;
      }
    }
    const keyRaw = (item.columns && item.columns.Key) || '';
    const key = keyRaw ? String(keyRaw).split(' ')[0] : (item.id || '');
    const name = item.name || (item.columns && item.columns.Name) || '';
    if (!key && !name) return null;
    return (
      <>
        {key && <span className="cell-key">{key}</span>}
        {key && name && <span className="cell-sep">: </span>}
        {name && <span className="cell-name">{name}</span>}
      </>
    );
  };

  // For a full-path row, find the deepest level that actually has an item.
  // If the chain ends before the last configured level, the next level is a
  // genuine missing trace (not just a deduplicated blank). Returns the display
  // column index where the "Missing" indicator belongs, or -1 if none.
  const missingColumnForRow = (row) => {
    // Prefer the backend's block-aware annotation (depths are contiguous from 1, so the
    // display column index is depth - 1). A short branch that legitimately ends is null.
    if (row && 'missing_at_depth' in row) {
      if (row.missing_at_depth == null) return -1;
      return row.missing_at_depth - 1;
    }
    let deepest = -1;
    for (let i = 0; i < levels.length; i++) {
      if (row[`l${i + 1}`]) deepest = i;
    }
    return deepest >= 0 && deepest < levels.length - 1 ? deepest + 1 : -1;
  };

  // Group rows by L1 and deduplicate like the full matrix view
  const groupByL1 = () => {
    const groups = {};
    rows.forEach(row => {
      if (row.l1) {
        const l1Id = row.l1.id;
        if (!groups[l1Id]) {
          groups[l1Id] = { l1: row.l1, children: [] };
        }
        groups[l1Id].children.push(row);
      }
    });
    return Object.values(groups);
  };

  const deduplicatedRows = [];
  groupByL1().forEach(group => {
    const prevLevels = {}; // level key -> last PRINTED row's item id

    group.children.forEach((row, rowInGroupIdx) => {
      const showL1 = rowInGroupIdx === 0;

      // A level prints when its item differs from the row above OR when a
      // shallower level already printed — a new parent starts a new chain, so a
      // child shared by two parents (one test case covering two requirements)
      // repeats under each. Mirrors MatrixPage.jsx.
      const levelVisible = {};
      let ancestorPrinted = false;
      for (let levelIdx = 0; levelIdx < levels.length; levelIdx++) {
        const levelKey = `l${levelIdx + 1}`;
        const currentId = row[levelKey]?.id ?? null;
        const visible = levelIdx === 0
          ? showL1
          : !!currentId && (ancestorPrinted || currentId !== (prevLevels[levelKey] ?? null));
        levelVisible[levelKey] = visible;
        if (visible) ancestorPrinted = true;
      }

      if (!Object.values(levelVisible).some(Boolean)) return;

      // Commit dedup state only for rows that actually render
      for (let levelIdx = 0; levelIdx < levels.length; levelIdx++) {
        const levelKey = `l${levelIdx + 1}`;
        prevLevels[levelKey] = row[levelKey]?.id ?? null;
      }

      // Build deduplicated row
      const dedupRow = {};
      for (let levelIdx = 0; levelIdx < levels.length; levelIdx++) {
        const levelKey = `l${levelIdx + 1}`;
        dedupRow[levelKey] = levelVisible[levelKey] ? row[levelKey] : null;
      }

      // Missing trace is derived from the ORIGINAL full-path row, not the
      // deduplicated display row, so blanked-out (deduped) cells are never
      // mistaken for gaps.
      deduplicatedRows.push({ ...dedupRow, missingAtIndex: missingColumnForRow(row) });
    });
  });

  return (
    <div className="matrix-preview-table">
      <table>
        <thead>
          <tr>
            {levels.map((level, i) => (
              <th key={`${level.name}-${i}`}>{level.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {deduplicatedRows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              {levels.map((level, colIdx) => {
                const isMissing = row.missingAtIndex === colIdx;
                return (
                  <td key={colIdx}>
                    {isMissing ? (
                      <span className="missing-indicator" title="No traceability at this level">
                        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Missing
                      </span>
                    ) : (
                      renderItem(row[`l${colIdx + 1}`], level.isTestRun)
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Dashboard;
